import React, { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Button, Stack, Typography } from '@mui/material';

import { lensIdFromPath, lensPath } from '~/pages/AskRichardUnified/lensPath';

import { useGetReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { type LensAppliedChange, refreshLensAfterChange } from '~/hooks/apis/reporting/useLensChange';

export default function LensChangeReceipt({
  receipt,
  openDestination = true,
}: {
  receipt: LensAppliedChange;
  openDestination?: boolean;
}) {
  const client = useQueryClient();
  const { data } = useGetReportingLeLens(receipt.lensId);
  const viewChange = receipt.view?.change;
  const shownVersion = data?.lens.version;
  const shownViewRevision = data?.view?.revision ?? 0;
  useEffect(() => {
    const behind = viewChange
      ? data !== undefined && shownViewRevision < viewChange
      : shownVersion !== undefined && shownVersion < receipt.toVersion;
    if (behind) {
      void refreshLensAfterChange(client, receipt.lensId);
    }
  }, [client, data, receipt.lensId, receipt.toVersion, shownVersion, shownViewRevision, viewChange]);
  const refused = receipt.problems.filter((problem) => problem.kind === 'refused');
  const offScreen =
    (lensIdFromPath(window.location.pathname) ?? new URLSearchParams(window.location.search).get('lens')) !==
    receipt.lensId;
  const canOpen = openDestination && receipt.applied && offScreen && data?.lens.id === receipt.lensId;
  return refused.length || canOpen ? (
    <Stack spacing={0.5}>
      {refused.map((problem) => (
        <Typography key={`${problem.path}-${problem.message}`} sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          Not changed: {problem.message}
        </Typography>
      ))}
      {canOpen && (
        <Button href={lensPath(receipt.lensId)} size="small" sx={{ alignSelf: 'flex-start' }}>
          Open Lens
        </Button>
      )}
    </Stack>
  ) : (
    <Typography variant="body2">
      {receipt.said?.length ? `${receipt.said.join('. ')}.` : receipt.view ? 'Updated your view' : 'Updated Lens'}
    </Typography>
  );
}
